import { FC, useState } from 'react';
import { Typography } from '@mui/material';
import { StandardDialog } from '@/components/common/StandardDialog';
import { FormTextField } from '@/components/common/FormComponents';
import { SampleDto, SampleActivityDto } from '@/api/ApiClient';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useBackend } from '@/hooks/useBackend';
import { log } from '@/lib/utils/logger';
import { getProblemDetail } from '@/lib/utils/apiErrors';

interface SplitDialogProps {
  open: boolean;
  onClose: () => void;
  sample: SampleDto;
  onSubmit?: (activity: SampleActivityDto) => void;
  loading?: boolean;
}

/**
 * Dialog component for splitting a sample into multiple pieces.
 * Allows users to specify the number of pieces to split into.
 */
const SplitDialog: FC<SplitDialogProps> = ({ open, onClose, sample, onSubmit, loading }) => {
  const [nbOfPieces, setNbOfPieces] = useState<number | undefined>(2);
  const [error, setError] = useState<string>('');
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();

  const handlePiecesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === '' ? undefined : parseInt(event.target.value);
    setNbOfPieces(value);

    if (value !== undefined && (isNaN(value) || value < 2)) {
      setError('Must split into at least 2 pieces');
    } else {
      setError('');
    }
  };

  const handleSubmit = async () => {
    if (error || !nbOfPieces) return;
    if (!sample) return;

    try {
      // Let the backend create the actual pieces.
      const pieces = await backend.sample.split(sample.id!, nbOfPieces);
      // This will also create a split activity,
      // but we have to load that now. All pieces will have the id, let's take the first.
      const activityId = pieces[0].parentActivityId;
      if (!activityId) {
        showSnackbar('Error creating split activity', 'error');
        return;
      }
      const splitActivity = await backend.sampleActivity.get(activityId);

      onSubmit?.(splitActivity);
      showSnackbar('Sample split successfully', 'success');
    } catch (error) {
      log.error('[AddSplitDialog] Error splitting sample:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to split sample.', 'error');
    } finally {
      onClose();
    }
  };

  return (
    <StandardDialog
      open={open}
      onClose={onClose}
      title="Split Sample"
      maxWidth="sm"
      onSubmit={handleSubmit}
      submitText="Split"
      submitDisabled={!nbOfPieces || !!error || loading}
    >
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Enter the number of pieces to split the sample into.
      </Typography>
      <FormTextField
        label="Number of Pieces"
        required
        autoFocus
        margin="dense"
        type="number"
        fullWidth
        value={nbOfPieces ?? ''}
        onChange={handlePiecesChange}
        error={!!error}
        helperText={error || 'Required'}
        disabled={loading}
        //FIXME Inputprops is depcreated
        inputProps={{ min: 2 }}
      />
    </StandardDialog>
  );
};

export { SplitDialog };
